import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import { IconMail, IconPlus, IconTrash, IconDeviceFloppy } from '@tabler/icons-react';
import type {
  PricingReview,
  PricingReviewInput,
  PricingReviewResults,
  PricingReviewCustomerRow,
  ProfitProjection,
  CatalogProduct,
} from '@techgitter/contracts';

import {
  fetchReviews,
  createReview,
  updateReview,
  deleteReview,
  fetchReviewResults,
} from '../services/pricingReviewService';
import { fetchProjections } from '../services/projectionsApi';
import { getProducts } from '../services/productCacheService';
import {
  SecondaryToolbar,
  ToolbarChip,
  ToolbarSelect,
  ToolbarDate,
  ToolbarButton,
  ToolbarSpacer,
} from '../components/toolbar';
import {
  DataTableCard,
  DataTable,
  DataTableHeadCell,
  DataTableRow,
  DataTableCell,
  DataTableEmpty,
} from '../components/table';
import { Chip } from '../components/chip';
import { CARD_SX, FIELD_SX, PRIMARY_BTN_SX, OUTLINED_BTN_SX } from '../styles/designSystem';
import { PricingEmailDialog } from '../components/pricing/PricingEmailDialog';
import { PRICING_EMAIL_VARIABLES, INCREASES_TABLE_TOKEN } from '../utils/pricingEmailRenderer';

type View = 'report' | 'email';
type SortKey = 'name' | 'current' | 'new' | 'increasing';
type SortDir = 'asc' | 'desc';

const DEFAULT_BODY = `Hi,

Microsoft is changing prices on several products from your next billing cycle. The
products affected on your account, with the new pricing, are below:

${INCREASES_TABLE_TOKEN}

Please reach out if you'd like to discuss. Kind regards,`;

const fmt = (n: number): string =>
  n.toLocaleString('en-AU', { style: 'currency', currency: 'AUD' });

interface Draft {
  name: string;
  targetFrom: string;
  targetTo: string;
  skuList: string[];
  projectionId: string;
  emailSubject: string;
  emailBody: string;
  notes: string;
}

const EMPTY_DRAFT: Draft = {
  name: '',
  targetFrom: '',
  targetTo: '',
  skuList: [],
  projectionId: '',
  emailSubject: 'Microsoft 365 Pricing Increases',
  emailBody: DEFAULT_BODY,
  notes: '',
};

function draftFromReview(r: PricingReview): Draft {
  return {
    name: r.name ?? '',
    targetFrom: r.targetFrom ?? '',
    targetTo: r.targetTo ?? '',
    skuList: r.skuList ?? [],
    projectionId: r.projectionId ?? '',
    emailSubject: r.emailSubject ?? '',
    emailBody: r.emailBody ?? '',
    notes: r.notes ?? '',
  };
}

function draftToInput(d: Draft): PricingReviewInput {
  return {
    name: d.name.trim(),
    targetFrom: d.targetFrom || null,
    targetTo: d.targetTo || null,
    skuList: d.skuList,
    projectionId: d.projectionId || null,
    emailSubject: d.emailSubject,
    emailBody: d.emailBody,
    notes: d.notes,
  };
}

export const ProductPricingPage: React.FC = () => {
  const [reviews, setReviews] = useState<PricingReview[]>([]);
  const [projections, setProjections] = useState<ProfitProjection[]>([]);
  const [products, setProducts] = useState<CatalogProduct[]>([]);

  const [selectedId, setSelectedId] = useState<string>('');
  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);
  const [savedDraft, setSavedDraft] = useState<Draft>(EMPTY_DRAFT);

  const [view, setView] = useState<View>('report');
  const [results, setResults] = useState<PricingReviewResults | null>(null);
  const [loadingResults, setLoadingResults] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [sort, setSort] = useState<{ key: SortKey; dir: SortDir }>({ key: 'current', dir: 'desc' });
  const [drillCustomer, setDrillCustomer] = useState<PricingReviewCustomerRow | null>(null);
  const [emailCustomer, setEmailCustomer] = useState<PricingReviewCustomerRow | null>(null);

  const dirty = useMemo(
    () => JSON.stringify(draft) !== JSON.stringify(savedDraft),
    [draft, savedDraft],
  );

  // --- Initial reference data ---
  useEffect(() => {
    const controller = new AbortController();
    (async () => {
      try {
        const [rs, ps] = await Promise.all([
          fetchReviews(controller.signal),
          fetchProjections(controller.signal),
        ]);
        if (controller.signal.aborted) return;
        setReviews(rs);
        setProjections(ps);
      } catch (e) {
        if (e instanceof Error && e.name === 'AbortError') return;
        setError(e instanceof Error ? e.message : 'Failed to load reviews');
      }
      try {
        const { items } = await getProducts();
        if (!controller.signal.aborted) setProducts(items);
      } catch {
        /* product picker is best-effort */
      }
    })();
    return () => controller.abort();
  }, []);

  const loadResults = useCallback(async (id: string) => {
    if (!id) {
      setResults(null);
      return;
    }
    setLoadingResults(true);
    setError(null);
    try {
      const r = await fetchReviewResults(id);
      setResults(r);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to compute results');
      setResults(null);
    } finally {
      setLoadingResults(false);
    }
  }, []);

  const selectReview = useCallback(
    (id: string) => {
      setSelectedId(id);
      const r = reviews.find((x) => x.id === id);
      const d = r ? draftFromReview(r) : EMPTY_DRAFT;
      setDraft(d);
      setSavedDraft(d);
      setResults(null);
      if (id) loadResults(id);
    },
    [reviews, loadResults],
  );

  const newReview = () => {
    setSelectedId('');
    setDraft(EMPTY_DRAFT);
    setSavedDraft(EMPTY_DRAFT);
    setResults(null);
    setView('report');
  };

  const handleSave = async () => {
    if (!draft.name.trim()) {
      setToast('Give the review a name first.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const input = draftToInput(draft);
      const saved = selectedId
        ? await updateReview(selectedId, input)
        : await createReview(input);
      const list = await fetchReviews();
      setReviews(list);
      setSelectedId(saved.id);
      const d = draftFromReview(saved);
      setDraft(d);
      setSavedDraft(d);
      setToast('Saved');
      loadResults(saved.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedId) return;
    if (!window.confirm(`Delete review "${draft.name}"?`)) return;
    try {
      await deleteReview(selectedId);
      setReviews((prev) => prev.filter((r) => r.id !== selectedId));
      newReview();
      setToast('Deleted');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to delete');
    }
  };

  // --- Product picker options ---
  const productOptions = useMemo(() => {
    const opts = products.map((p) => ({
      sku: p.sku,
      label: p.displayName ? `${p.displayName} (${p.sku})` : p.sku,
      m365: p.category === 'licensing-m365',
    }));
    // Surface M365 products first — they're the common case for this report.
    opts.sort((a, b) => Number(b.m365) - Number(a.m365) || a.label.localeCompare(b.label));
    return opts;
  }, [products]);

  const selectedOptions = useMemo(() => {
    const bySku = new Map(productOptions.map((o) => [o.sku, o]));
    return draft.skuList.map(
      (sku) => bySku.get(sku) ?? { sku, label: sku, m365: false },
    );
  }, [draft.skuList, productOptions]);

  // --- Sorted customer rows ---
  const sortedCustomers = useMemo(() => {
    const rows = results?.customers ?? [];
    const factor = sort.dir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      switch (sort.key) {
        case 'name':
          return factor * (a.customerName ?? '').localeCompare(b.customerName ?? '');
        case 'new':
          return factor * (a.totalNewValue - b.totalNewValue);
        case 'increasing':
          return factor * (a.increaseCount - b.increaseCount);
        case 'current':
        default:
          return factor * (a.totalCurrentValue - b.totalCurrentValue);
      }
    });
  }, [results, sort]);

  const toggleSort = (key: SortKey) =>
    setSort((s) =>
      s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' },
    );

  const totals = results?.totals;

  return (
    <>
      <Box sx={{ px: 2, pt: '10px' }}>
        <Typography sx={{ fontSize: 12, color: '#888', mb: 1.5, maxWidth: 760 }}>
          Plan customer comms for supplier price changes (e.g. M365). Pick the products and a
          date range, choose a what-if scenario for the new prices, then drill into each customer
          and copy a tailored email. Prices are shown ex-GST.
        </Typography>
        {totals && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <KpiTile label="Customers" value={String(totals.customerCount)} color="#4A90D9" />
            <KpiTile label="With increases" value={String(totals.affectedCustomerCount)} color="#EF9F27" />
            <KpiTile label="Lines increasing" value={String(totals.increaseCount)} color="#9B59B6" />
            <KpiTile label="Current value" value={fmt(totals.totalCurrentValue)} color="#16A085" />
            <KpiTile label="New value" value={fmt(totals.totalNewValue)} color="#16A085" />
          </Box>
        )}
      </Box>

      {/* Toolbar: pick / create review, switch view, save / delete */}
      <SecondaryToolbar floating>
        <ToolbarSelect
          label="Review"
          value={selectedId}
          onChange={selectReview}
          options={[
            { value: '', label: 'New review…' },
            ...reviews.map((r) => ({ value: r.id, label: r.name || '(unnamed)' })),
          ]}
        />
        <ToolbarButton icon={IconPlus} onClick={newReview}>
          New
        </ToolbarButton>
        <ToolbarChip label="Report" active={view === 'report'} onClick={() => setView('report')} />
        <ToolbarChip label="Email template" active={view === 'email'} onClick={() => setView('email')} />
        <ToolbarSpacer />
        {selectedId && (
          <ToolbarButton icon={IconTrash} onClick={handleDelete}>
            Delete
          </ToolbarButton>
        )}
        <ToolbarButton icon={IconDeviceFloppy} onClick={handleSave}>
          {saving ? 'Saving…' : dirty || !selectedId ? 'Save' : 'Saved'}
        </ToolbarButton>
      </SecondaryToolbar>

      <Box sx={{ p: '12px 16px 24px' }}>
        {error && (
          <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {view === 'report' ? (
          <>
            {/* Criteria */}
            <Box sx={[CARD_SX, { p: 2, mb: 2 }]}>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                <TextField
                  label="Review name"
                  value={draft.name}
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                  size="small"
                  sx={{ ...FIELD_SX, flex: '2 1 240px' }}
                />
                <ToolbarDate
                  label="Due from"
                  value={draft.targetFrom}
                  onChange={(v) => setDraft({ ...draft, targetFrom: v })}
                />
                <ToolbarDate
                  label="Due to"
                  value={draft.targetTo}
                  onChange={(v) => setDraft({ ...draft, targetTo: v })}
                />
                <TextField
                  select
                  label="Scenario"
                  value={draft.projectionId}
                  onChange={(e) => setDraft({ ...draft, projectionId: e.target.value })}
                  size="small"
                  SelectProps={{ native: true }}
                  sx={{ ...FIELD_SX, flex: '1 1 200px' }}
                >
                  <option value="">— select scenario —</option>
                  {projections.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </TextField>
              </Box>
              <Autocomplete
                multiple
                size="small"
                sx={{ mt: 2 }}
                options={productOptions}
                value={selectedOptions}
                isOptionEqualToValue={(o, v) => o.sku === v.sku}
                getOptionLabel={(o) => o.label}
                onChange={(_e, vals) => setDraft({ ...draft, skuList: vals.map((v) => v.sku) })}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Products (SKUs to watch)"
                    placeholder="Add products…"
                    sx={FIELD_SX}
                  />
                )}
              />
            </Box>

            {/* Results */}
            {dirty || !selectedId ? (
              <Alert severity="info">
                {selectedId
                  ? 'Save the review to refresh the customer results.'
                  : 'Name and save the review to compute customer results.'}
              </Alert>
            ) : loadingResults ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress size={28} />
              </Box>
            ) : (
              <SummaryTable
                rows={sortedCustomers}
                sort={sort}
                onSort={toggleSort}
                onDrill={setDrillCustomer}
                onEmail={setEmailCustomer}
              />
            )}
          </>
        ) : (
          <EmailTemplateEditor
            subject={draft.emailSubject}
            body={draft.emailBody}
            notes={draft.notes}
            onChange={(patch) => setDraft({ ...draft, ...patch })}
          />
        )}
      </Box>

      {drillCustomer && (
        <DrillDownDialog
          customer={drillCustomer}
          onClose={() => setDrillCustomer(null)}
          onEmail={() => {
            setEmailCustomer(drillCustomer);
            setDrillCustomer(null);
          }}
        />
      )}

      <PricingEmailDialog
        open={!!emailCustomer}
        customer={emailCustomer}
        subjectTemplate={draft.emailSubject}
        bodyTemplate={draft.emailBody}
        onClose={() => setEmailCustomer(null)}
      />

      <Snackbar
        open={!!toast}
        autoHideDuration={3000}
        onClose={() => setToast(null)}
        message={toast ?? ''}
      />
    </>
  );
};

const KpiTile: React.FC<{ label: string; value: string; color: string }> = ({ label, value, color }) => (
  <Box
    sx={[
      CARD_SX,
      {
        p: '12px 16px',
        flex: '1 1 150px',
        minWidth: 140,
        borderLeft: '3px solid',
        borderLeftColor: color,
      },
    ]}
  >
    <Typography sx={{ fontSize: 24, fontWeight: 600, color: '#1a1a1a', lineHeight: 1.1 }}>
      {value}
    </Typography>
    <Typography
      sx={{
        fontSize: 11,
        fontWeight: 500,
        color: '#888',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        mt: 0.5,
      }}
    >
      {label}
    </Typography>
  </Box>
);

const SortHead: React.FC<{
  label: string;
  col: SortKey;
  sort: { key: SortKey; dir: SortDir };
  onSort: (k: SortKey) => void;
  align?: 'left' | 'right';
}> = ({ label, col, sort, onSort, align = 'left' }) => (
  <DataTableHeadCell
    align={align}
    sortable
    active={sort.key === col}
    direction={sort.dir}
    onSort={() => onSort(col)}
  >
    {label}
  </DataTableHeadCell>
);

const ROW_BTN_SX = { ...OUTLINED_BTN_SX, textTransform: 'none', fontSize: 12, py: 0.25, px: 1, minWidth: 0 };

const SummaryTable: React.FC<{
  rows: PricingReviewCustomerRow[];
  sort: { key: SortKey; dir: SortDir };
  onSort: (k: SortKey) => void;
  onDrill: (c: PricingReviewCustomerRow) => void;
  onEmail: (c: PricingReviewCustomerRow) => void;
}> = ({ rows, sort, onSort, onDrill, onEmail }) => (
  <DataTableCard>
    <DataTable>
      <thead>
        <tr>
          <SortHead label="Customer" col="name" sort={sort} onSort={onSort} />
          <DataTableHeadCell>Email</DataTableHeadCell>
          <SortHead label="Increasing" col="increasing" sort={sort} onSort={onSort} align="right" />
          <SortHead label="Current value" col="current" sort={sort} onSort={onSort} align="right" />
          <SortHead label="New value" col="new" sort={sort} onSort={onSort} align="right" />
          <DataTableHeadCell align="right">Actions</DataTableHeadCell>
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 ? (
          <DataTableEmpty colSpan={6}>
            No customers match — check the date range, products, and scenario.
          </DataTableEmpty>
        ) : (
          rows.map((c) => (
            <DataTableRow key={c.customerId} onClick={() => onDrill(c)}>
              <DataTableCell bold>{c.customerName ?? c.customerId}</DataTableCell>
              <DataTableCell muted>{c.customerEmail ?? '— none —'}</DataTableCell>
              <DataTableCell align="right">
                {c.increaseCount > 0 ? (
                  <Chip tone="warning">{c.increaseCount}</Chip>
                ) : (
                  <Chip>0</Chip>
                )}
              </DataTableCell>
              <DataTableCell align="right">{fmt(c.totalCurrentValue)}</DataTableCell>
              <DataTableCell align="right">{fmt(c.totalNewValue)}</DataTableCell>
              <DataTableCell align="right">
                <Button
                  size="small"
                  variant="outlined"
                  sx={ROW_BTN_SX}
                  startIcon={<IconMail size={14} stroke={1.5} />}
                  disabled={c.increaseCount === 0}
                  onClick={(e) => {
                    e.stopPropagation();
                    onEmail(c);
                  }}
                >
                  Email
                </Button>
              </DataTableCell>
            </DataTableRow>
          ))
        )}
      </tbody>
    </DataTable>
  </DataTableCard>
);

const DrillDownDialog: React.FC<{
  customer: PricingReviewCustomerRow;
  onClose: () => void;
  onEmail: () => void;
}> = ({ customer, onClose, onEmail }) => (
  <Dialog open onClose={onClose} maxWidth="md" fullWidth>
    <DialogTitle>{customer.customerName ?? customer.customerId}</DialogTitle>
    <DialogContent dividers>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        <strong>Invoice email:</strong> {customer.customerEmail ?? '— none on customer record —'}
      </Typography>
      <DataTableCard>
        <DataTable>
          <thead>
            <tr>
              <DataTableHeadCell>Product / SKU</DataTableHeadCell>
              <DataTableHeadCell>Term</DataTableHeadCell>
              <DataTableHeadCell align="right">Qty</DataTableHeadCell>
              <DataTableHeadCell align="right">Current (ex GST)</DataTableHeadCell>
              <DataTableHeadCell align="right">New (ex GST)</DataTableHeadCell>
              <DataTableHeadCell align="right">Change</DataTableHeadCell>
            </tr>
          </thead>
          <tbody>
            {customer.lines.map((l, i) => (
              <DataTableRow key={`${l.sku}-${i}`}>
                <DataTableCell>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <span>{l.productName ?? l.sku}</span>
                    {l.productName && (
                      <span style={{ fontSize: 11, color: '#888' }}>{l.sku}</span>
                    )}
                  </Box>
                </DataTableCell>
                <DataTableCell>{l.cadence === 'annual' ? 'Annual' : 'Monthly'}</DataTableCell>
                <DataTableCell align="right">{l.quantity}</DataTableCell>
                <DataTableCell align="right">{fmt(l.currentSell)}</DataTableCell>
                <DataTableCell align="right">
                  {l.newSell !== null ? fmt(l.newSell) : '—'}
                </DataTableCell>
                <DataTableCell align="right">
                  {l.isIncrease ? (
                    <Chip tone="warning">
                      +{(((l.newSell! - l.currentSell) / (l.currentSell || 1)) * 100).toFixed(1)}%
                    </Chip>
                  ) : (
                    '—'
                  )}
                </DataTableCell>
              </DataTableRow>
            ))}
          </tbody>
        </DataTable>
      </DataTableCard>
    </DialogContent>
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, p: 2 }}>
      <Button onClick={onClose} sx={OUTLINED_BTN_SX}>
        Close
      </Button>
      <Button
        variant="contained"
        sx={PRIMARY_BTN_SX}
        startIcon={<IconMail size={16} stroke={1.5} />}
        onClick={onEmail}
        disabled={customer.increaseCount === 0}
      >
        Email
      </Button>
    </Box>
  </Dialog>
);

const EmailTemplateEditor: React.FC<{
  subject: string;
  body: string;
  notes: string;
  onChange: (patch: Partial<Draft>) => void;
}> = ({ subject, body, notes, onChange }) => (
  <Box sx={[CARD_SX, { p: 2 }]}>
    <Typography sx={{ fontSize: 13, color: '#888', mb: 2 }}>
      Boilerplate reused for every customer. The increases table for each customer is inserted
      where you place <code>{INCREASES_TABLE_TOKEN}</code>. Other placeholders below.
    </Typography>
    <TextField
      label="Subject"
      value={subject}
      onChange={(e) => onChange({ emailSubject: e.target.value })}
      size="small"
      fullWidth
      sx={{ ...FIELD_SX, mb: 2 }}
    />
    <TextField
      label="Body"
      value={body}
      onChange={(e) => onChange({ emailBody: e.target.value })}
      size="small"
      fullWidth
      multiline
      minRows={10}
      sx={{ ...FIELD_SX, mb: 2 }}
    />
    <TextField
      label="Notes (internal — not emailed)"
      value={notes}
      onChange={(e) => onChange({ notes: e.target.value })}
      size="small"
      fullWidth
      multiline
      minRows={2}
      sx={{ ...FIELD_SX, mb: 2 }}
    />
    <Box>
      <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#666', mb: 0.5 }}>
        Available placeholders
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {PRICING_EMAIL_VARIABLES.map((v) => (
          <Chip key={v.token} title={v.label}>
            {v.token}
          </Chip>
        ))}
      </Box>
    </Box>
  </Box>
);

export default ProductPricingPage;
